import { CommerceService } from "@/services/commerce"
import { AppSettings } from "@/settings"

const commerceApi = CommerceService.api()
const config = AppSettings.ctpConfig()
const ignoreBeforeQuery = config.cartIgnoreBeforeUtc
  ? `and (createdAt >= "${config.cartIgnoreBeforeUtc}" or lastModifiedAt >= "${config.cartIgnoreBeforeUtc}")`
  : ``

/**
 * Method to retrieve customer cart based on id or key
 *
 * @param {string} id
 * @param {string} key
 *
 * @return {Promise<cart>|null}
 */
export const getCart = async (id, key) => {
  if (typeof id !== "string" && typeof key !== "string") return null
  const isKey = typeof key === "string"

  let cart = {}

  if (isKey) cart = (await commerceApi.carts().withKey({ key: key }).get().execute())?.body
  else cart = (await commerceApi.carts().withId({ ID: id }).get().execute())?.body

  return cart ?? null
}

/**
 * Method to retrieve active customer carts
 *
 * @param {string} customerId
 * @param {string} employeeNumber
 *
 * @return {Promise<array>|null}
 */
export const getActiveCustomerCart = async (customerId, employeeNumber) => {
  const activeCustomerCarts = (
    await commerceApi
      .carts()
      .get({
        queryArgs: {
          limit: 1,
          withTotal: false,
          sort: ["lastModifiedAt desc"],
          where: `customerId="${customerId}" and cartState="Active" and custom(fields(createdByEmployeeNumber="${employeeNumber}")) ${ignoreBeforeQuery}`,
        },
      })
      .execute()
  )?.body.results

  return activeCustomerCarts
}

/**
 * Method to retrieve active customer cart skus
 *
 * @param {string} customerId
 * @param {string} employeeNumber
 *
 * @return {Promise<array>|null}
 */
export const getActiveCustomerCartSkus = async (customerId, employeeNumber) => {
  const activeCustomerCarts = (
    await commerceApi
      .carts()
      .get({
        queryArgs: {
          limit: 1,
          withTotal: false,
          sort: ["lastModifiedAt desc"],
          where: `customerId="${customerId}" and cartState="Active" and custom(fields(createdByEmployeeNumber="${employeeNumber}")) ${ignoreBeforeQuery}`,
        },
      })
      .execute()
  )?.body.results

  if (!activeCustomerCarts || activeCustomerCarts[0]?.customLineItems?.length < 1) return null

  const activeCustomerCartSkus = activeCustomerCarts[0].customLineItems.map(({ custom }) => custom?.fields?.sku)

  return activeCustomerCartSkus
}

/**
 * Finds and calculat skonto discount on the cart
 *
 * @param {string} skontoId
 * @param {object} skontoContainer
 * @param {number} totalGrossCent
 *
 * @returns {number|null}
 */
export const getCartDiscountOption = (skontoId, skontoContainer, totalGrossCent) => {
  if (!skontoId || skontoId?.length === 0) return null
  if (!skontoContainer) return defaultZeroSokoReturn(totalGrossCent)

  const skontoText = JSON.parse(skontoContainer?.value)?.skontoText

  if (!skontoText || skontoText?.length === 0) return defaultZeroSokoReturn(totalGrossCent)

  // Extract percentage values using a regular expression (takes any digit before a % sign) and find biggest number
  const percentages = skontoText.match(/\d+(?=%)/g)?.map(Number)
  if (!percentages || percentages?.length === 0) return defaultZeroSokoReturn(totalGrossCent)
  const maxPercentage = Math.max(...percentages)

  if (!maxPercentage || maxPercentage === 0) return defaultZeroSokoReturn(totalGrossCent)

  const centDiscount = (maxPercentage / 100) * totalGrossCent
  const rawDiscount = centDiscount / 100
  const sokoTotal = (totalGrossCent - centDiscount) / 100

  return {
    discount: +(Math.round(rawDiscount + "e+2") + "e-2"),
    percent: Math.round(maxPercentage), // to int value
    sokoTotal: +(Math.round(sokoTotal + "e+2") + "e-2"),
  }
}

const defaultZeroSokoReturn = (totalGrossCent) => {
  const grossFormatted = totalGrossCent / 100

  return {
    discount: 0,
    percent: 0,
    sokoTotal: +(Math.round(grossFormatted + "e+2") + "e-2"),
  }
}
